import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { FaFileDownload } from "react-icons/fa";
import logo from "../../../../assets/images/footer-logo.png";
import footerLogo from "../../../../assets/images/Logo-no-bg - Copy.png";
import BodySlipImg from "../../../../assets/images/slip-banner.png";
import BottomBarImg from "../../../../assets/images/bottom-bar.png";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import axios from "axios";
import {
  buildPaymentSlipPdfBlob,
  buildPaymentSlipPdfBlobForEmail,
  downloadPdfBlob,
} from "../../../payments/paymentSlipPdf";
import {
  resolveParcelId,
  uploadPaymentSlipPdf,
} from "../../../payments/paymentSlipBackend";
import {
  clearPaymentSlipUploadSessionFlags,
  getPaymentSlipUploadSessionKey,
  slipDebug,
  slipDebugError,
  slipDebugWarn,
} from "../../../payments/paymentSlipDebug";
import {
  formatCurrency,
  resolveCurrencySymbol,
} from "../../../../utils/formatCurrency";
import {
  MANUAL_BANK_ACCOUNT,
  MANUAL_PAYMENT_TYPE_ID,
  formatManualPaymentNote,
} from "../../../../utils/manualPayment";

const PaymentSlip = ({
  paymentDataOverride = null,
  disableAutoUpload = false,
  embedded = false,
}) => {
  const receiptRef = useRef(null);
  const location = useLocation();
  const [paymentData, setPaymentData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const paymentDataRef = useRef(null);
  const autoUploadFiredRef = useRef(false);
  const renderLoggedRef = useRef(false);
  const backendUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    slipDebug("mount", "PaymentSlip component mounted", {
      pathname: location.pathname,
      apiUrl: backendUrl,
    });
  }, []);

  useEffect(() => {
    paymentDataRef.current = paymentData;
    slipDebug("paymentDataRef", "ref updated", {
      hasPaymentData: !!paymentData,
      parcelId: paymentData?.parcelId,
      senderEmail: paymentData?.senderEmail,
      receiverEmail: paymentData?.receiverEmail,
      recordSerialNo: paymentData?.recordSerialNo,
    });
  }, [paymentData]);

  // Fetch countries and cities (does not block slip render / upload)
  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const countriesResponse = await axios.get(`${backendUrl}Country`);
        setCountries(countriesResponse.data);

        const citiesResponse = await axios.get(`${backendUrl}city`);
        setCities(citiesResponse.data);
      } catch (error) {
        console.error("Error fetching location data:", error);
      } finally {
        setLocationsLoading(false);
      }
    };

    fetchLocationData();
  }, [backendUrl]);

  // Set payment data (optional override for admin embedded slip)
  useEffect(() => {
    if (paymentDataOverride) {
      setPaymentData(paymentDataOverride);
      return;
    }

    const state = location.state;
    const hasState =
      state &&
      typeof state === "object" &&
      Object.keys(state).length > 0 &&
      (state.parcelId ||
        state.receiverEmail ||
        state.senderEmail ||
        state.bookingBarcodeNumber);

    if (hasState) {
      slipDebug("loadPaymentData", "using location.state", {
        parcelId: state.parcelId,
        senderEmail: state.senderEmail,
        receiverEmail: state.receiverEmail,
        bookingBarcodeNumber: state.bookingBarcodeNumber,
      });
      clearPaymentSlipUploadSessionFlags(state.parcelId, state.recordSerialNo);
      setPaymentData(state);
      return;
    }

    const storedData = localStorage.getItem("paymentSlipData");
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        slipDebug("loadPaymentData", "using localStorage paymentSlipData", {
          parcelId: parsed.parcelId,
          senderEmail: parsed.senderEmail,
          receiverEmail: parsed.receiverEmail,
          recordSerialNo: parsed.recordSerialNo,
          keys: Object.keys(parsed),
        });
        clearPaymentSlipUploadSessionFlags(
          parsed.parcelId,
          parsed.recordSerialNo,
        );
        setPaymentData(parsed);
      } catch (error) {
        slipDebugError(
          "loadPaymentData",
          "invalid paymentSlipData JSON",
          error,
        );
      }
    } else {
      slipDebugWarn(
        "loadPaymentData",
        "STOP — no location.state and no paymentSlipData in localStorage",
      );
    }
  }, [location.state, paymentDataOverride]);

  const waitForReceiptElement = (maxAttempts = 15, delayMs = 200) =>
    new Promise((resolve, reject) => {
      slipDebug("waitForReceiptElement", "waiting for receiptRef...", {
        maxAttempts,
        delayMs,
      });
      let attempts = 0;
      const tryResolve = () => {
        const element = receiptRef.current;
        if (element) {
          slipDebug("waitForReceiptElement", `OK on attempt ${attempts + 1}`);
          resolve(element);
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          slipDebugError(
            "waitForReceiptElement",
            "STOP — receiptRef never attached",
            {
              attempts,
            },
          );
          reject(new Error("Payment slip is not ready to export."));
          return;
        }
        if (attempts === 1 || attempts % 5 === 0) {
          slipDebug(
            "waitForReceiptElement",
            `retry ${attempts}/${maxAttempts}`,
          );
        }
        setTimeout(tryResolve, delayMs);
      };
      tryResolve();
    });

  const generateAndUploadPdf = useCallback(
    async ({ download = false, silent = false } = {}) => {
      slipDebug("generateAndUploadPdf", "START", { download, silent });

      const data = paymentDataRef.current;
      if (!data) {
        slipDebugWarn(
          "generateAndUploadPdf",
          "STOP — paymentDataRef.current is null",
        );
        if (!silent) toast.error("Payment slip data is missing.");
        return;
      }

      const parcelId = resolveParcelId(data);
      if (!parcelId) {
        slipDebugWarn("generateAndUploadPdf", "STOP — parcelId missing", {
          dataKeys: Object.keys(data),
        });
        if (!silent) {
          toast.error("Missing parcel id. Cannot upload payment slip.");
        }
        return;
      }

      setPdfBusy(true);
      try {
        slipDebug("generateAndUploadPdf", "building PDF...");
        const element = await waitForReceiptElement();
        const slipMeta = {
          bookingBarcodeNumber: data.bookingBarcodeNumber,
          recordSerialNo: data.recordSerialNo,
        };

        if (download) {
          const { blob: downloadBlob, fileName: downloadFileName } =
            await buildPaymentSlipPdfBlob(element, slipMeta);
          slipDebug("generateAndUploadPdf", "download PDF built", {
            fileName: downloadFileName,
            blobSize: downloadBlob?.size,
          });
          slipDebug("generateAndUploadPdf", "triggering local download");
          downloadPdfBlob(downloadBlob, downloadFileName);
        }

        const { blob, fileName } = await buildPaymentSlipPdfBlobForEmail(
          element,
          slipMeta,
        );
        slipDebug("generateAndUploadPdf", "email PDF built for upload", {
          fileName,
          blobSize: blob?.size,
        });

        try {
          slipDebug("generateAndUploadPdf", "calling uploadPaymentSlipPdf...");
          await uploadPaymentSlipPdf(parcelId, blob, fileName, {
            recordSerialNo: data.recordSerialNo,
            paymentSlipData: data,
          });
          sessionStorage.setItem(
            getPaymentSlipUploadSessionKey(parcelId, data.recordSerialNo),
            "1",
          );
          slipDebug("generateAndUploadPdf", "upload complete");

          if (!silent) {
            toast.success(
              download
                ? "Payment slip downloaded and sent to the server."
                : "Payment slip sent to the server.",
            );
          }
        } catch (uploadError) {
          slipDebugError("generateAndUploadPdf", "upload failed", uploadError);
          if (!silent) {
            const message =
              uploadError?.response?.data?.message ||
              uploadError?.response?.data?.title ||
              uploadError?.message ||
              "Failed to send payment slip to the server.";
            toast.error(
              download
                ? `PDF downloaded, but upload failed: ${message}`
                : message,
            );
          }
          if (!download) {
            throw uploadError;
          }
        }
      } catch (error) {
        slipDebugError("generateAndUploadPdf", "PDF generation failed", error);
        if (!silent) {
          toast.error(error?.message || "Failed to generate payment slip PDF.");
        }
        if (download) {
          throw error;
        }
      } finally {
        setPdfBusy(false);
        slipDebug("generateAndUploadPdf", "END");
      }
    },
    [],
  );

  const handleDownloadPDF = async () => {
    slipDebug("handleDownloadPDF", "button clicked");
    try {
      await generateAndUploadPdf({ download: true, silent: false });
    } catch (err) {
      slipDebugError("handleDownloadPDF", "finished with error", err);
    }
  };

  useEffect(() => {
    if (disableAutoUpload) return;

    slipDebug("autoUploadEffect", "effect ran", {
      hasPaymentData: !!paymentData,
      receiptRefAttached: !!receiptRef.current,
    });

    if (!paymentData) {
      slipDebug("autoUploadEffect", "STOP — no paymentData yet");
      return;
    }

    const parcelId = resolveParcelId(paymentData);
    if (!parcelId) {
      slipDebugWarn("autoUploadEffect", "STOP — parcelId missing");
      return;
    }

    if (autoUploadFiredRef.current) {
      slipDebug(
        "autoUploadEffect",
        "STOP — auto-upload already ran this page visit",
      );
      return;
    }

    slipDebug("autoUploadEffect", "scheduling auto-upload in 500ms", {
      parcelId,
    });

    let cancelled = false;

    const timer = setTimeout(() => {
      if (cancelled) {
        slipDebugWarn(
          "autoUploadEffect",
          "timer fired but effect was cancelled",
        );
        return;
      }
      if (autoUploadFiredRef.current) return;
      autoUploadFiredRef.current = true;
      slipDebug(
        "autoUploadEffect",
        "timer fired — starting generateAndUploadPdf",
      );
      generateAndUploadPdf({ download: false, silent: true }).catch((err) => {
        autoUploadFiredRef.current = false;
        slipDebugWarn("autoUploadEffect", "generateAndUploadPdf rejected", err);
      });
    }, 500);

    return () => {
      slipDebug("autoUploadEffect", "cleanup — cancel timer");
      cancelled = true;
      clearTimeout(timer);
    };
  }, [paymentData, generateAndUploadPdf, disableAutoUpload]);

  const getCountry = (countryId) => {
    if (!countryId) return "";
    const country = countries.find((item) => item.id === parseInt(countryId));
    return country?.name || "";
  };

  const getCity = (cityId) => {
    if (!cityId) return "";
    const city = cities.find((item) => item.id === parseInt(cityId));
    return city?.name || "";
  };

  if (!paymentData) {
    slipDebug("render", "showing empty state (no paymentData)");
    return (
      <div
        className={
          embedded
            ? "flex justify-center items-center py-16"
            : "flex justify-center items-center h-screen"
        }
      >
        <div className="text-xl font-semibold">
          {locationsLoading
            ? "Loading payment details..."
            : "No payment slip data found. Please complete a booking first."}
        </div>
      </div>
    );
  }

  if (!renderLoggedRef.current) {
    renderLoggedRef.current = true;
    slipDebug("render", "showing slip UI", {
      parcelId: paymentData.parcelId,
      receiptRefAttached: !!receiptRef.current,
    });
  }

  const {
    transitCost = [],
    bookingBarcodeNumber,
    currencyType = "GBP",
    currencyTypeId,
    producDetails = [],
    fetchedFormData = {},
    shipmentArrivalDate,
    parcelId,
    recordSerialNo,
    ...bookingInfo
  } = paymentData;

  const currencySymbol = resolveCurrencySymbol(
    paymentData.currencyTypes?.find((c) => c.id === currencyTypeId)?.symbol,
    currencyType,
  );

  const formatAmount = (amount) =>
    formatCurrency(amount, currencySymbol, { currencyType, space: false });

  const formatDate = () => {
    if (!shipmentArrivalDate) return "";
    return `${shipmentArrivalDate.month} ${shipmentArrivalDate.date}, ${shipmentArrivalDate.year}`;
  };

  // Calculate aggregated totals from all items
  const convertedCosts =
    transitCost && transitCost.length > 0
      ? {
          disCountedPercentageAmount: transitCost.reduce(
            (sum, item) =>
              sum + (parseFloat(item.disCountedPercentageAmount) || 0),
            0,
          ),
          disCountedPercentage: transitCost[0]?.disCountedPercentage || 0,
          afterDiscountCost: transitCost.reduce(
            (sum, item) => sum + (parseFloat(item.afterDiscountCost) || 0),
            0,
          ),
          totalCost: transitCost.reduce(
            (sum, item) => sum + (parseFloat(item.totalCost) || 0),
            0,
          ),
          totalCostWithoutVAT: transitCost.reduce(
            (sum, item) => sum + (parseFloat(item.totalCostWithoutVAT) || 0),
            0,
          ),
        }
      : {
          disCountedPercentageAmount: 0,
          disCountedPercentage: 0,
          afterDiscountCost: 0,
          totalCost: 0,
          totalCostWithoutVAT: 0,
        };
  console.log(convertedCosts.totalCost);
  return (
    <div className={embedded ? "w-full" : "max-w-4xl mx-auto p-4"}>
      <div className="flex justify-end mb-4 gap-2 no-print">
        <button
          type="button"
          onClick={handleDownloadPDF}
          disabled={pdfBusy}
          className="bg-orange-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-orange-600 transition-colors disabled:opacity-60"
        >
          <FaFileDownload />
          {pdfBusy ? "Preparing PDF..." : "Download PDF"}
        </button>
      </div>

      <div
        ref={receiptRef}
        className="relative overflow-hidden pb-0 border border-gray-200 rounded-lg receipt-container"
        style={{
          width: "210mm",
          margin: "0 auto",
        }}
      >
        <img
          src={BodySlipImg}
          alt=""
          data-slip-bg
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover pointer-events-none"
          style={{ zIndex: 0 }}
        />
        <div className="relative z-10">
          {/* New Custom Header */}
          <div className="mt-5 w-full mb-6 print-section">
            {/* Custom Header matching provided design */}
            <div className="flex items-center overflow-hidden">
              {/* Logo shape: rectangle with curved right edge */}
              <div className="bg-black px-6 py-3 flex justify-center w-80">
                <img src={logo} width={150} alt="Company Logo" />
              </div>

              {/* Orange bar: rectangle with curved left edge */}
              <div className="flex-1 -ml-9">
                <div
                  className="h-12 px-4 py-3 bg-orange-500 w-full border-white "
                  style={{
                    borderWidth: "8px",
                    borderTopLeftRadius: "40px",
                    borderBottomLeftRadius: "40px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Shipping Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 print-section">
            <div className="border border-gray-200 bg-white p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-orange-500 border-b border-gray-200 pb-2 mb-3">
                Sender Details
              </h2>
              <p className="font-medium">{bookingInfo?.senderName}</p>
              <p>{bookingInfo?.senderAddress}</p>
              <p>
                {getCity(bookingInfo?.senderCity)},{" "}
                {getCountry(bookingInfo?.senderCountry)}
              </p>
              <p className="mt-2">
                <span className="font-semibold">Email:</span>{" "}
                {bookingInfo?.senderEmail}
              </p>
              <p>
                <span className="font-semibold">Phone:</span>{" "}
                {bookingInfo?.senderMobileNumber}
              </p>
            </div>
            <div className="border border-gray-200 bg-white p-4 rounded-lg">
              <h2 className="text-xl font-semibold text-orange-500 border-b border-gray-200 pb-2 mb-3">
                Receiver Details
              </h2>
              <p className="font-medium">{bookingInfo?.receiverName}</p>
              <p>{bookingInfo?.receiverAddress}</p>
              <p>
                {getCity(bookingInfo?.receiverCity)},
                {getCountry(bookingInfo?.receiverCountry)}
              </p>
              <p className="mt-2">
                <span className="font-semibold">Email:</span>
                {bookingInfo?.receiverEmail}
              </p>
              <p>
                <span className="font-semibold">Phone:</span>
                {bookingInfo?.receiverMobileNumber}
              </p>
            </div>
          </div>

          {/* Shipment Details */}
          <div className="border border-gray-200 bg-white p-4 rounded-lg mb-8 print-section">
            <h2 className="text-xl font-semibold text-orange-500 border-b border-gray-200 pb-2 mb-3">
              Shipment Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="font-semibold">Parcel Number:</p>
                <p>{recordSerialNo || "Pending"}</p>
              </div>
              {/* <div>
                            <p className="font-semibold">Shipment Date:</p>
                            <p>{formatDate()}</p>
                        </div> */}
              <div>
                <p className="font-semibold">Parcel Dimensions:</p>
                <div>
                  {producDetails && producDetails.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {producDetails.map((item, index) => {
                        const itemLength =
                          item.itemLength !== undefined &&
                          item.itemLength !== ""
                            ? item.itemLength
                            : index === 0
                              ? bookingInfo?.productLength
                              : "";
                        const itemWidth =
                          item.itemWidth !== undefined && item.itemWidth !== ""
                            ? item.itemWidth
                            : index === 0
                              ? bookingInfo?.productWidth
                              : "";
                        const itemHeight =
                          item.itemHeight !== undefined &&
                          item.itemHeight !== ""
                            ? item.itemHeight
                            : index === 0
                              ? bookingInfo?.productHeight
                              : "";
                        const itemWeight =
                          item.itemWeight !== undefined &&
                          item.itemWeight !== ""
                            ? item.itemWeight
                            : index === 0
                              ? bookingInfo?.productWeight
                              : "";
                        const preset = item.preset || bookingInfo?.preset || "";

                        return (
                          <p key={index} className="text-sm">
                            {producDetails.length > 1 && (
                              <span className="font-semibold">
                                Box {index + 1}:{" "}
                              </span>
                            )}
                            {preset ? `${preset} ` : ""}
                            {itemLength && itemWidth && itemHeight
                              ? `(${itemLength}x${itemWidth}x${itemHeight} in)`
                              : ""}
                            {itemWeight ? ` - ${itemWeight}kg` : ""}
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <p>
                      {bookingInfo?.preset || ""} (
                      {bookingInfo?.productLength || ""}x
                      {bookingInfo?.productWidth || ""}x
                      {bookingInfo?.productHeight || ""} in) -{" "}
                      {bookingInfo?.productWeight || ""}kg
                    </p>
                  )}
                </div>
              </div>
              <div>
                <p className="font-semibold">Payment Method:</p>
                <p>
                  {bookingInfo?.paymentTypeId === "1"
                    ? "Cash"
                    : bookingInfo?.paymentTypeId === "2"
                      ? "Online Payment"
                      : String(bookingInfo?.paymentTypeId) ===
                          MANUAL_PAYMENT_TYPE_ID
                        ? "Bank Payment"
                        : "N/A"}
                </p>
                {bookingInfo?.paymentInvoiceNo ? (
                  <p className="text-sm text-gray-700 mt-1">
                    Invoice:{" "}
                    <span className="font-semibold">
                      {bookingInfo.paymentInvoiceNo}
                    </span>
                  </p>
                ) : null}
                {bookingInfo?.cardLast4 ? (
                  <p className="text-sm text-gray-700 mt-1">
                    {bookingInfo.cardBrand ? `${bookingInfo.cardBrand} ` : ""}
                    •••• {bookingInfo.cardLast4}
                  </p>
                ) : null}
              </div>
              <div>
                <p className="font-semibold">Product Value:</p>
                <div>
                  {producDetails && producDetails.length > 0 ? (
                    <div className="flex flex-col gap-1">
                      {producDetails.map((item, index) => (
                        <p key={index} className="text-sm">
                          {producDetails.length > 1 && (
                            <span className="font-semibold">
                              Box {index + 1}:{" "}
                            </span>
                          )}
                          {formatAmount(item.productValue || 0)}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p>{formatAmount(producDetails[0]?.productValue || 0)}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {(() => {
            const isManualBankPayment =
              String(bookingInfo?.paymentTypeId) === MANUAL_PAYMENT_TYPE_ID;

            const paymentSummaryCard = (
              <div className="border border-gray-200 bg-white p-4 rounded-lg h-full print-section">
                <h2 className="text-xl font-semibold text-orange-500 border-b border-gray-200 pb-2 mb-3">
                  Payment Summary ({currencyType})
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-black uppercase tracking-wider">
                          Amount ({currencySymbol})
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Number(convertedCosts.disCountedPercentageAmount) >
                        0 && (
                        <tr>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-orange-500">
                            Discount (
                            {Number(convertedCosts.disCountedPercentage) > 0
                              ? `${convertedCosts.disCountedPercentage}%`
                              : "N/A"}
                            )
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 text-right">
                            {formatAmount(
                              -Number(
                                convertedCosts.disCountedPercentageAmount,
                              ),
                            )}
                          </td>
                        </tr>
                      )}
                      <tr className="bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-orange-500">
                          Subtotal
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-bold text-orange-500 text-right">
                          {formatAmount(
                            convertedCosts.totalCostWithoutVAT || 0,
                          )}
                        </td>
                      </tr>
                      <tr className="bg-blue-50">
                        <td className="px-4 py-3 whitespace-nowrap text-base font-bold text-blue-800">
                          Total Amount
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-base font-bold text-blue-800 text-right">
                          {formatAmount(
                            convertedCosts.totalCostWithoutVAT || 0,
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            );

            const accountInfoCard = (
              <div className="border border-gray-200 bg-white rounded-lg overflow-hidden h-full print-section">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-xl font-semibold text-orange-500">
                    A/C INFORMATION
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Use this page as your invoice for manual bank transfer.
                  </p>
                </div>
                <div className="p-4 space-y-2 text-[15px] text-gray-900">
                  <p>
                    <span className="font-semibold">Company Name:</span>{" "}
                    {MANUAL_BANK_ACCOUNT.companyName}
                  </p>
                  <p>
                    <span className="font-semibold">A/C no</span>{" "}
                    {MANUAL_BANK_ACCOUNT.accountNumber}
                  </p>
                  <p>
                    <span className="font-semibold">Sortcode</span>
                    {MANUAL_BANK_ACCOUNT.sortCode}.
                    {MANUAL_BANK_ACCOUNT.bankName}
                  </p>
                  {bookingInfo?.paymentInvoiceNo ? (
                    <div className="mt-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">
                        Invoice number
                      </p>
                      <p className="text-xl font-bold tracking-wider text-orange-900 select-all">
                        {bookingInfo.paymentInvoiceNo}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            );

            return (
              <>
                {/* Product Details */}
                <div className="border border-gray-200 bg-white p-4 rounded-lg mb-8 print-section">
                  <h2 className="text-xl font-semibold text-orange-500 border-b border-gray-200 pb-2 mb-3">
                    Product Details
                  </h2>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                            #
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                            Product Group
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                            Item Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                            Value ({currencySymbol})
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider">
                            Qty
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {producDetails?.map((product, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-orange-500">
                              {index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {product.productGroupName || "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {product.productTypeName || "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {product.itemDescription || "N/A"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {formatAmount(product.productValue || 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {product.productQty || "0"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {isManualBankPayment ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-stretch print-section">
                      <div>{accountInfoCard}</div>
                      <div>{paymentSummaryCard}</div>
                    </div>
                    <div className="bg-red-600 text-white px-4 py-3 text-sm font-semibold rounded-lg mb-8 print-section">
                      NOTES:{" "}
                      {bookingInfo?.paymentInvoiceNo
                        ? formatManualPaymentNote(bookingInfo.paymentInvoiceNo)
                        : "Please attach invoice number in payment reference."}
                    </div>
                  </>
                ) : (
                  <div className="mb-8">{paymentSummaryCard}</div>
                )}
              </>
            );
          })()}
          {/* Footer content — compact, transparent so slip background shows */}
          <div className="w-full bg-transparent py-3 px-5 flex justify-between items-center gap-4">
            {/* Contact Info */}
            <div className="flex flex-col gap-2.5 text-[13px] leading-snug text-gray-900">
              {/* Phone */}
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white flex-shrink-0">
                  <Icon icon="mdi:phone" width="16" />
                </div>
                <div className="flex flex-col font-medium">
                  <span>+44 7903 375754</span>
                  <span>+03338002439</span>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-white flex-shrink-0">
                  <Icon icon="mdi:send" width="16" />
                </div>
                <div className="flex flex-col font-medium">
                  <span>info@sundarbancargo.com</span>
                  <span>www.sundarbancargo.com</span>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-center gap-2.5 max-w-md">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white flex-shrink-0">
                  <Icon icon="mdi:home" width="16" />
                </div>
                <div className="flex flex-col font-medium">
                  {/* <span>9 Village Green Road, Crayford, DA1 4X</span> */}
                  <span>Rear Ground Floor</span>
                  <span>124 Whitechapel Road London E1 1JE</span>
                </div>
              </div>
            </div>

            {/* Logo */}
            <div className="flex items-center flex-shrink-0 pr-1">
              <img
                src={footerLogo}
                alt="Company Logo"
                className="w-[140px] h-auto object-contain"
              />
            </div>
          </div>

          {/* Bottom bar – FULL WIDTH */}
          <img
            src={BottomBarImg}
            style={{ height: "36px", objectFit: "cover" }}
            alt="Bottom bar"
            className="w-full block"
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentSlip;
